import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { adminsApi } from '@/api/endpoints/admins'
import { useUI } from '@/stores/ui'

interface Props {
  open: boolean
  onClose: () => void
  onCreated?: (admin?: { email: string; password?: string }) => void
}

function generatePassword(length: number = 10): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  return Array.from({ length }, () => alphabet[Math.floor(Math.random() * alphabet.length)]).join('')
}

export default function CreateAdminModal({ open, onClose, onCreated }: Props) {
  const addToast = useUI((state) => state.addToast)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [canManage, setCanManage] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const handleGeneratePassword = () => {
    setPassword(generatePassword(10))
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await adminsApi.create({
        email,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password: password || undefined,
        can_manage_admins: canManage,
      })
      addToast({ message: 'Admin created successfully', type: 'success' })
      
      // Pass password to onCreated callback if available (for email sending)
      const adminPassword = password || undefined
      
      // Reset form
      setFirstName('')
      setLastName('')
      setEmail('')
      setPassword('')
      setCanManage(false)
      onCreated?.({ email, password: adminPassword })
      onClose()
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to create admin',
        type: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 hover-raise"
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
          >
            <h3 className="text-lg font-semibold mb-2">Add Admin</h3>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input className="input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                <input className="input-field focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </div>
              <input type="email" className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              <div className="flex gap-2">
                <input className="input-field flex-1 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 transition-shadow" placeholder="Temp password (auto-generated if empty)" value={password} onChange={(e) => setPassword(e.target.value)} />
                <button type="button" className="btn-secondary text-xs whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-yellow-400/60" onClick={handleGeneratePassword} title="Generate a random 10-character password using letters and numbers">Generate</button>
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={canManage} onChange={(e) => setCanManage(e.target.checked)} />
                <span>Designated admin (can manage admins)</span>
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" className="btn-secondary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]" onClick={onClose} disabled={submitting}>Cancel</button>
                <button type="submit" className="btn-primary transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-yellow-400/60" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}



